import { inject, Injectable } from '@angular/core';
import { WordRect } from '../page-overlay/model';
import { OCR_DATA_PROVIDER_TOKEN, OcrData } from '../model/ocr-data-provider.interface';
import { from, map, mergeMap, Observable, of } from 'rxjs';

/**
 * This service provides helper functions and simple algorithms to
 * extract data from the ocr layer of a page.
 * Basically what we do is a lot of rect-rect intersection checking, where
 * all positions in this service are expected to be in [0, 1] representing
 * a width / height percentage (of a page).
 */
@Injectable({
  providedIn: 'root',
})
export class OcrService {
  private readonly ocrDataProvider = inject(OCR_DATA_PROVIDER_TOKEN);

  private readonly MAX_CONCURRENT_REQUESTS = 10;

  private ocrDataByPageId: Record<
    string,
    {
      wordsByRect: Map<WordRect, string>;
    }
  > = {};

  loadOcrDataForPageIds(pageIds: Iterable<string>): Observable<unknown> {
    return from(pageIds).pipe(
      mergeMap((pageId) => {
        if (this.ocrDataByPageId[pageId]) {
          return of();
        }

        return this.ocrDataProvider.provideOcrData(pageId).pipe(
          map((ocrData) => {
            this.ocrDataByPageId[pageId] = { wordsByRect: this.getWordPositionMap(ocrData) };
          }),
        );
      }, this.MAX_CONCURRENT_REQUESTS),
    );
  }

  private getWordPositionMap(ocrData: OcrData) {
    const rectWordPairs = ocrData.lines.flatMap((line) => {
      return line.words.map((wordEntry) => {
        const p = wordEntry.position;
        const wordRect: WordRect = {
          x: [p.left, p.left + p.width],
          y: [p.top, p.top + p.height],
        };
        return [wordRect, wordEntry.text] as const;
      });
    });

    return new Map<WordRect, string>(rectWordPairs);
  }

  getWord(pageId: string, rect: WordRect): string {
    const wordsByRect = this.ocrDataByPageId[pageId].wordsByRect;
    return wordsByRect.get(rect) as string;
  }

  getWordsInRect(pageId: string, rect: WordRect): WordRect[] {
    const elements = this.ocrDataByPageId[pageId];

    if (!elements) {
      return [];
    }

    const words = [...elements.wordsByRect.keys()];
    return words.filter((wordRect) => this.areRectsIntersecting(wordRect, rect));
  }

  private areRectsIntersecting(r1: WordRect, r2: WordRect): boolean {
    return r1.x[1] > r2.x[0] && r1.x[0] < r2.x[1] && r1.y[1] > r2.y[0] && r1.y[0] < r2.y[1];
  }
}
